import { Link, useParams } from 'react-router-dom';
import { useProject } from '../hooks/useProject';

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString();
}

export function ProjectDetailPage() {
  const { jobNumber } = useParams<{ jobNumber: string }>();
  const { data: project, isLoading, error } = useProject(jobNumber ?? null);

  if (isLoading) {
    return (
      <section className="bg-white rounded-lg shadow p-6 max-w-5xl mx-auto space-y-4">
        <div className="h-8 w-64 bg-slate-100 rounded animate-pulse" />
        <div className="h-4 w-96 bg-slate-100 rounded animate-pulse" />
        <div className="h-32 bg-slate-100 rounded animate-pulse" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white rounded-lg shadow p-6 max-w-5xl mx-auto">
        <p className="text-red-600 text-sm">Something went wrong. Try again, or contact your admin if the problem persists.</p>
      </section>
    );
  }

  if (!project) return null;

  return (
    <section className="bg-white rounded-lg shadow p-6 max-w-5xl mx-auto space-y-4">
      <Link to="/projects" className="text-brand-blue text-sm hover:underline">← Back to Projects</Link>

      <h1 className="text-2xl font-bold text-brand-charcoal">
        [{project.jobNumber}] – {project.name}
      </h1>

      <div className="flex gap-2 flex-wrap mt-2">
        {project.serviceLine && (
          <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">{project.serviceLine}</span>
        )}
        {project.account && (
          <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">{project.account}</span>
        )}
        {project.region && (
          <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">{project.region}</span>
        )}
        <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">
          {project.isBillable ? 'Billable' : 'Non-billable'}
        </span>
      </div>

      {(project.projectedStart || project.projectedEnd) && (
        <p className="text-sm text-slate-600">
          Projected {formatDate(project.projectedStart)} – {formatDate(project.projectedEnd)}
        </p>
      )}

      {project.boxFolderName && (
        <p>
          <span className="text-brand-blue hover:underline text-sm cursor-pointer">Open in Box ↗</span>
        </p>
      )}

      <p className="text-xs text-slate-500 italic">
        This view is read-only. To update assignments, make changes in the source report and wait for the next ingest.
      </p>

      <h2 className="text-lg font-semibold text-brand-charcoal mt-6">Crew Assignments</h2>

      {project.assignments.length === 0 ? (
        <p className="text-slate-500 text-sm">No crew assignments on record for this project.</p>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">Planned Dates</th>
              </tr>
            </thead>
            <tbody>
              {project.assignments.map((a, i) => {
                // Assignments arrive sorted by person, so a repeat of the previous row's person is
                // an additional stretch of work — show the name only once.
                const isRepeat = i > 0 && project.assignments[i - 1].personId === a.personId;
                return (
                  <tr key={a.assignmentId} className={isRepeat ? '' : 'border-t border-slate-100'}>
                    <td className="py-2 px-3 font-medium text-brand-charcoal">
                      {isRepeat ? '' : a.personDisplayName}
                    </td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{a.plannedStart} – {a.plannedEnd}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
